import React, { useMemo, useCallback } from "react";
import {
  FormHelperText,
  FormLabel,
  Box,
  FormControl,
  Modal,
} from "@mui/material";
import SignaturePad from "react-signature-canvas";
import { useJsonForms } from "@jsonforms/react";
import {
  ModalSignatureContainer,
  SignatureInfo,
} from "../../styles/jss/components/forms/form-signature";
import Button from "../../components/Button/Button";
import { SignatureType } from "../../enum/SignatureType";
import { SignatureUtil } from "../../util/SignatureUtil";
import { SchemaUtils } from "../../util/SchemaUtils";
import { CloseSaveModal } from "../../styles/jss/components/savemodal";

// Modal canvas padding/offset to account for header, footer, and margins
const MODAL_CANVAS_HEIGHT_OFFSET = 710;

const Signature = (props: any) => {
  const [hasSignature, setHasSignature] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [pageSignaturePad, setPageSignaturePad] = React.useState<any>();
  const [modalSignaturePad, setModalSignaturePad] = React.useState<any>();
  const [error, setError] = React.useState({ code: -1, message: "" });
  const [modalCanvasSize, setModalCanvasSize] = React.useState({
    height: Math.max(document.body.clientHeight - MODAL_CANVAS_HEIGHT_OFFSET, 200),
    width: document.body.clientWidth - 20,
  });
  const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

  const { uiSchema, onEnd, path, rootSchema, schema, appValidated } = props;
  const jsonForms = useJsonForms();

  // Simple signature presence check - only when needed
  const checkSignaturePresence = useCallback((signaturePad: any) => {
    if (signaturePad) {
      return !signaturePad.isEmpty();
    }
    return false;
  }, []);

  // Only check signature presence on mount and when pads change
  React.useEffect(() => {
    if (pageSignaturePad) {
      const existingSignature = SignatureUtil.getExistingSignature(getComponentKey()) || "";
      const hasExistingSignature = existingSignature !== "";
      const hasCurrentSignature = !pageSignaturePad.isEmpty();
      setHasSignature(hasExistingSignature || hasCurrentSignature);
    }
  }, [pageSignaturePad, uiSchema.scope]);

  React.useEffect(() => {
    const handleResize = () => {
      setModalCanvasSize({
        height: document.body.clientHeight - MODAL_CANVAS_HEIGHT_OFFSET,
        width: document.body.clientWidth - 20,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useEffect(() => {
    if (appValidated) {
      setAppHasBeenValidated(true);
    }
  }, [appValidated]);

  function handleSignatureEnd(file?: string) {
    if (file === undefined || file === null) {
      setError({
        code: 1,
        message: `signature file is empty, please try again.`,
      });
    } else {
      setError({
        code: -1,
        message: ""
      });
      onEnd(file);
    }
  }

  const modalStyle = {
    position: "fixed",
    bgcolor: "background.paper",
    boxShadow: 24,
    height: "100%",
    width: "100%",
    p: 2,
  };

  let showDescription =
      schema.description !== undefined && schema.description !== "";

  let visible = SchemaUtils.isComponentVisible(uiSchema, jsonForms.core!.data);
  let enabled = SchemaUtils.isComponentEnabled(uiSchema, jsonForms.core!.data);
  let required = SchemaUtils.isRequired(path, rootSchema);

  const showRequiredError = useMemo(() =>
          required && appHasBeenValidated && !hasSignature,
      [required, appHasBeenValidated, hasSignature]
  );

  const showError = useMemo(() =>
          error !== undefined && error.message !== "",
      [error]
  );

  const hasError = showError || showRequiredError;
  const errorMessage = showRequiredError ? 'This field is required' : error.message;

  const penColor = uiSchema.options?.signatureColor || 'black';

  // Enable/disable signature pad
  React.useEffect(() => {
    if (pageSignaturePad) {
      if (enabled) {
        pageSignaturePad.on();
      } else {
        pageSignaturePad.clear();
        pageSignaturePad.off();
        setHasSignature(false);
      }
    }
  }, [pageSignaturePad, enabled]);

  const handleOpenModal = () => {
    if (pageSignaturePad) {
      pageSignaturePad.clear();
    }
    setHasSignature(false);
    handleSignatureEnd("");
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const clearPageSignaturePad = () => {
    if (pageSignaturePad) {
      pageSignaturePad.clear();
      setHasSignature(false);
    }
    handleSignatureEnd("");
  };

  const redoSignature = () => {
    SignatureUtil.removeExistingSignature(getComponentKey());
    if (pageSignaturePad) {
      pageSignaturePad.clear();
    }
    setHasSignature(false);
    handleSignatureEnd("");
  };

  const clearModalSignaturePad = () => {
    if (modalSignaturePad) {
      modalSignaturePad.clear();
    }
    if (pageSignaturePad) {
      pageSignaturePad.clear();
    }
    setHasSignature(false);
    handleSignatureEnd("");
  };

  // Simple handlers without real-time interference
  const handlePageSignatureEnd = () => {
    if (!pageSignaturePad) return;

    const signatureFile = pageSignaturePad.getCanvas().toDataURL();
    const hasSig = !pageSignaturePad.isEmpty();
    setHasSignature(hasSig); // Single update after drawing
    handleSignatureEnd(hasSig ? signatureFile : "");
  };

  // Canvas dimension helper
  const getPageCanvasDimensions = () => (
      {
        height: window.innerWidth < 600 ? window.innerWidth * 0.395 : 600 * 0.395,
        width: document.body.clientWidth < 600 ? document.body.clientWidth - 40 : 600
      }
  );

  const handleModalSignatureEnd = () => {
    if (!modalSignaturePad) return;

    const signatureFile = modalSignaturePad.getCanvas().toDataURL();
    const hasSig = !modalSignaturePad.isEmpty();
    setHasSignature(hasSig); // Single update after drawing

    if (hasSig && pageSignaturePad) {
      const copyProps = getPageCanvasDimensions();
      pageSignaturePad.fromDataURL(signatureFile, { ...copyProps });
    } else if (pageSignaturePad) {
      pageSignaturePad.clear();
    }

    handleSignatureEnd(hasSig ? signatureFile : "");
  };

  const getComponentKey = () => {
    return uiSchema.scope;
  };

  const initSignatureCanvas = (type: SignatureType) => {
    if (type === SignatureType.Page) {
      const existingSignature = SignatureUtil.getExistingSignature(getComponentKey()) || "";
      if (existingSignature !== "") {
        return (
            <img
                style={{
                  maxWidth: window.innerWidth > 600 ? `600px` : "100%",
                  maxHeight: `300px`,
                  objectFit: "contain"
                }}
                key={getComponentKey()}
                src={existingSignature}
                alt="Signature"
            />
        );
      }
    }

    let canvasProps = {};
    if (type === SignatureType.Page) {
      const dimensions = getPageCanvasDimensions();
      canvasProps = {
        height: dimensions.height,
        width: dimensions.width,
        style: {
          backgroundColor: "#fbfbf8",
          border: hasError ? "solid 1px #d32f2f" : "solid 1px #00000073", // Red border for error, default otherwise
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`
        }
      };
    } else if (type === SignatureType.Modal) {
      canvasProps = {
        height: modalCanvasSize.height,
        width: modalCanvasSize.width,
        style: {
          backgroundColor: "#fbfbf8",
          border: "solid 1px #00000073",
          width: `${modalCanvasSize.width}px`,
          height: `${modalCanvasSize.height}px`
        }
      };
    }

    return (
        <SignaturePad
            penColor={penColor}
            minDistance={5}
            onEnd={type === SignatureType.Page ? handlePageSignatureEnd : handleModalSignatureEnd}
            canvasProps={{ ...canvasProps }}
            ref={(ref) => {
              if (type === SignatureType.Page) {
                setPageSignaturePad(ref);
              } else {
                setModalSignaturePad(ref);
              }
            }}
        />
    );
  };

  function initSignatureCtaSection(type: SignatureType) {
    if (!enabled) {
      return "";
    }
    const existingSignature = SignatureUtil.getExistingSignature(getComponentKey()) || "";
    const modalLink = type === SignatureType.Page
        ? existingSignature !== "" ? "Redo" : "Open in Modal"
        : "Close Modal";

    return (
        <Box>
          <Button
              variant="text"
              size="small"
              onClick={type === SignatureType.Page
                  ? existingSignature !== "" ? redoSignature : handleOpenModal
                  : handleCloseModal
              }>
            {modalLink}
          </Button>
          {type === SignatureType.Modal || existingSignature === "" ? (
              <Button
                  variant="text"
                  size="small"
                  onClick={type === SignatureType.Page ? clearPageSignaturePad : clearModalSignaturePad}>
                Clear
              </Button>
          ) : null}
        </Box>
    );
  }

  return (
      <Box sx={{ display: visible ? "block" : "none" }}>
        <FormControl className={`form-control`} fullWidth={true}>
          <FormLabel required={required} color="secondary" className="form-separate-label">
            {uiSchema.label}
          </FormLabel>
          {initSignatureCanvas(SignatureType.Page)}
          <SignatureInfo>
            <div className={"helper"}>
              <FormHelperText className={"description"}>
                {showDescription ? schema.description : null}
              </FormHelperText>
              {hasError && (
                  <FormHelperText error={true}>
                    {errorMessage}
                  </FormHelperText>
              )}
            </div>
            {initSignatureCtaSection(SignatureType.Page)}
          </SignatureInfo>
          <Modal
              open={open}
              onClose={handleCloseModal}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description">
            <Box sx={modalStyle}>
              <CloseSaveModal
                  type="button"
                  aria-label="Close"
                  onClick={handleCloseModal}>
                <span aria-hidden="true">&times;</span>
              </CloseSaveModal>
              <ModalSignatureContainer>
                <FormLabel className="form-separate-label" id="modal-modal-title">
                  {uiSchema.label}
                </FormLabel>
                {initSignatureCanvas(SignatureType.Modal)}
                <SignatureInfo>
                  <div className={"helper"}>
                    <FormHelperText id="modal-modal-description" className={"description"}>
                      {showDescription ? schema.description : null}
                    </FormHelperText>
                    <FormHelperText className={"error"} error={hasError}>
                      {hasError ? errorMessage : null}
                    </FormHelperText>
                  </div>
                  {initSignatureCtaSection(SignatureType.Modal)}
                </SignatureInfo>
              </ModalSignatureContainer>
            </Box>
          </Modal>
        </FormControl>
      </Box>
  );
};

export default Signature;
